// ─────────────────────────────────────────────────────────────────
// Sehat AI — InputBar Component
// Two modes:
//   1. Text mode  — type or push-to-talk mic (default)
//   2. Voice mode — hands-free conversation loop
// ─────────────────────────────────────────────────────────────────

import React from 'react';
import { Mic, MicOff, Send, PhoneCall, X, AlertTriangle, Loader2, Mic2 } from 'lucide-react';
import SoundWave from './SoundWave';

// Animated sound bars for voice mode
function VoiceBars({ color = '#FF6B35', count = 5 }) {
  const heights = [6, 14, 9, 18, 8];
  return (
    <div style={{ display: 'flex', gap: 3, alignItems: 'center', height: 22 }}>
      {heights.slice(0, count).map((h, i) => (
        <div
          key={i}
          style={{
            width: 3, height: h, background: color, borderRadius: 3,
            animation: `sw 0.6s ease ${i * 0.1}s infinite alternate`,
          }}
        />
      ))}
    </div>
  );
}

// ── Voice Conversation Mode Panel ─────────────────────────────────
function VoicePanel({ listening, interim, isSpeaking, loading, onEnd }) {
  let statusText, statusColor, statusBg, borderColor;

  if (isSpeaking) {
    statusText   = 'Sehat AI bol raha hai...';
    statusColor  = '#FF6B35';
    statusBg     = '#FFF7F5';
    borderColor  = '#FFD8CC';
  } else if (loading) {
    statusText   = 'Samajh raha hoon...';
    statusColor  = '#8B5CF6';
    statusBg     = '#F5F3FF';
    borderColor  = '#DDD6FE';
  } else if (listening) {
    statusText   = 'Sun raha hoon... boliye ji 🎤';
    statusColor  = '#059669';
    statusBg     = '#ECFDF5';
    borderColor  = '#6EE7B7';
  } else {
    statusText   = 'Taiyaar hoon... 🙏';
    statusColor  = '#6B7280';
    statusBg     = '#F9FAFB';
    borderColor  = '#E5E7EB';
  }

  return (
    <div style={{
      background: statusBg,
      border: `2px solid ${borderColor}`,
      borderRadius: 20,
      padding: '14px 16px',
      transition: 'all 0.3s',
      minHeight: 72,
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
    }}>
      {/* Status row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 12, flexShrink: 0,
          background: statusColor + '18',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {isSpeaking   ? <SoundWave color={statusColor} /> :
           loading       ? <Loader2 size={18} color={statusColor} style={{ animation: 'spin 1s linear infinite' }} /> :
           listening     ? <VoiceBars color={statusColor} /> :
                           <Mic2 size={18} color={statusColor} />}
        </div>

        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: statusColor, fontFamily: "'Nunito',sans-serif" }}>
            {statusText}
          </div>
          {listening && !interim && (
            <div style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 600 }}>
              Aap ki awaaz ka intezaar hai...
            </div>
          )}
        </div>

        {/* End voice mode */}
        <button
          onClick={onEnd}
          title="End voice conversation"
          style={{
            flexShrink: 0, background: '#FEE2E2', border: 'none',
            borderRadius: 10, padding: '6px 11px', cursor: 'pointer',
            fontSize: 11.5, color: '#DC2626', fontWeight: 800,
            fontFamily: "'Nunito',sans-serif",
          }}
        >
          <X size={13} /> End
        </button>
      </div>

      {/* Live interim transcript */}
      {interim && (
        <div style={{
          fontSize: 13, color: '#374151', fontStyle: 'italic',
          background: '#fff', borderRadius: 10, padding: '7px 12px',
          border: '1px solid #E5E7EB', lineHeight: 1.5,
          fontFamily: "'Nunito',sans-serif", fontWeight: 600,
        }}>
          "{interim}"
        </div>
      )}
    </div>
  );
}

// ── Main InputBar ─────────────────────────────────────────────────
export default function InputBar({
  // Text mode
  input, onChange, onSend, onMicStart, onMicStop, isListening, lang, loading,
  // TTS state
  isSpeaking, onStopSpeaking,
  // Voice conversation mode
  convMode, convListening, convInterim, onStartConv, onEndConv, convSupported,
}) {
  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  return (
    <div style={{ background: '#fff', borderTop: '1px solid #F3F4F6', boxShadow: '0 -4px 18px rgba(0,0,0,0.05)' }}>

      {/* TTS speaking bar (shown in text mode only — voice mode handles it inline) */}
      {!convMode && isSpeaking && (
        <div style={{ padding: '9px 16px', background: '#FFF7F5', borderBottom: '1px solid #FFD8CC', display: 'flex', alignItems: 'center', gap: 10 }}>
          <SoundWave color="#FF6B35" />
          <span style={{ fontSize: 12, color: '#FF6B35', fontWeight: 800 }}>Sehat AI bol raha hai...</span>
          <button onClick={onStopSpeaking} style={{ marginLeft: 'auto', fontSize: 11, color: '#9CA3AF', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'Nunito',sans-serif", fontWeight: 700 }}>
            Rok do ✕
          </button>
        </div>
      )}

      {/* Push-to-talk listening bar (text mode only) */}
      {!convMode && isListening && (
        <div style={{ padding: '9px 16px', background: '#FFF5F5', borderBottom: '1px solid #FCA5A5', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#EF4444', animation: 'pulse 0.7s infinite' }} />
          <span style={{ fontSize: 12, color: '#DC2626', fontWeight: 800 }}>Sun raha hoon... ab boliye 🎤</span>
        </div>
      )}

      <div style={{ padding: '11px 14px 15px' }}>
        {/* Emergency strip */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 9 }}>
          <div style={{ fontSize: 11.5, color: '#EF4444', background: '#FEF2F2', padding: '5px 16px', borderRadius: 16, border: '1px solid #FECACA', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
            <AlertTriangle size={13} style={{ flexShrink: 0 }} /> Emergency?&nbsp;
            <a href="tel:112" style={{ color: '#DC2626', textDecoration: 'none', fontWeight: 900 }}>Call 112</a>
            &nbsp;immediately
          </div>
        </div>

        {/* ── Voice Conversation Mode ── */}
        {convMode ? (
          <VoicePanel
            listening={convListening}
            interim={convInterim}
            isSpeaking={isSpeaking}
            loading={loading}
            onEnd={onEndConv}
          />
        ) : (
          /* ── Text Mode ── */
          <div style={{
            display: 'flex', gap: 9, alignItems: 'flex-end',
            background: '#FFF8F5', borderRadius: 20,
            border: `2px solid ${isListening ? '#EF4444' : '#FFD8CC'}`,
            padding: '9px 9px 9px 16px', transition: 'border-color 0.2s',
            boxShadow: '0 2px 10px rgba(255,107,53,0.08)',
          }}>
            <textarea
              value={input}
              onChange={e => onChange(e.target.value)}
              onKeyDown={handleKey}
              placeholder={lang === 'hi' ? 'अपनी तकलीफ यहाँ लिखें...' : 'Describe your health concern here...'}
              rows={1}
              style={{
                flex: 1, background: 'none', border: 'none', outline: 'none',
                fontSize: 13.5, fontFamily: "'Nunito',sans-serif",
                color: '#111827', resize: 'none', lineHeight: 1.6,
                paddingTop: 4, fontWeight: 600, caretColor: '#FF6B35',
              }}
            />

            {/* Voice conversation toggle */}
            {convSupported && (
              <button
                onClick={onStartConv}
                title="Start hands-free voice conversation"
                style={{
                  width: 42, height: 42, borderRadius: 14, border: 'none', flexShrink: 0,
                  background: 'linear-gradient(135deg,#6366F1,#8B5CF6)',
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(99,102,241,0.35)',
                  transition: 'all 0.2s',
                }}
              >
                <PhoneCall size={19} color="#fff" />
              </button>
            )}

            {/* Push-to-talk mic */}
            <button
              onClick={isListening ? onMicStop : onMicStart}
              style={{
                width: 42, height: 42, borderRadius: 14, border: 'none', flexShrink: 0,
                background: isListening ? '#EF4444' : '#FFF0EB',
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: isListening ? '0 0 0 5px rgba(239,68,68,0.2)' : 'none',
                transition: 'all 0.2s',
              }}
            >
              {isListening ? <MicOff size={20} color="#fff" /> : <Mic size={20} color="#FF6B35" />}
            </button>

            {/* Send */}
            <button
              onClick={onSend}
              disabled={!input.trim() || loading}
              style={{
                width: 42, height: 42, borderRadius: 14, border: 'none', flexShrink: 0,
                background: input.trim() && !loading ? 'linear-gradient(135deg,#FF6B35,#F7931E)' : '#F1F5F9',
                cursor: input.trim() ? 'pointer' : 'default',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: input.trim() && !loading ? '0 4px 14px rgba(255,107,53,0.35)' : 'none',
                transition: 'all 0.2s',
              }}
            >
              <Send size={17} color={input.trim() && !loading ? '#fff' : '#CBD5E1'} />
            </button>
          </div>
        )}

        <div style={{ textAlign: 'center', marginTop: 7, fontSize: 10, color: '#D1D5DB', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
          {convMode
            ? <><Mic2 size={11} /> Hands-free mode · Boliye, main sun rahi hoon</>
            : 'Apni takleef batayein — main madad karungi'}
        </div>
      </div>
    </div>
  );
}
